import React, { useState, useEffect, useMemo, useRef } from 'react';
import VirtualList from 'rc-virtual-list';
import {
    Table, Button, Modal, Form, Input, Select, DatePicker, message, Popconfirm, Space, Card, Typography,
    Row, Col, Tag, Upload, InputNumber, Switch, Divider, Dropdown, Tabs, Badge
} from 'antd';
import {
    PlusOutlined, EditOutlined, DeleteOutlined, EyeOutlined, DownloadOutlined,
    FilePdfOutlined, FileImageOutlined, ReloadOutlined, SettingOutlined, SwapOutlined, FileExcelOutlined, CheckOutlined, CloseOutlined
} from '@ant-design/icons';
import {
    hoSoNhanVienService,
    LoaiGiayToDto,
    GiayToDto,
    CreateGiayToRequest,
    UpdateGiayToRequest,
    CreateLoaiGiayToRequest,
    UpdateLoaiGiayToRequest,
    NhanVienAnhDaiDienDto,
} from '../services/hoSoNhanVienService';
import { masterDataService, Employee, Department, ChucDanh, ChucVu, ChuyenMon } from '../services/masterDataService';
import dayjs, { Dayjs } from 'dayjs';

const { Title, Text } = Typography;
const { Option } = Select;
const { RangePicker } = DatePicker;
const { TabPane } = Tabs;

interface ExpiryStatus {
    color: string;
    text: string;
}

const getExpiryStatus = (ngayHetHan: string | null): ExpiryStatus => {
    if (!ngayHetHan) return { color: 'blue', text: 'Vĩnh viễn' };
    const exp = dayjs(ngayHetHan);
    const now = dayjs();
    const diffDays = exp.diff(now, 'day');
    if (diffDays < 0) return { color: 'red', text: 'Đã hết hạn' };
    if (diffDays <= 30) return { color: 'orange', text: `Còn ${diffDays} ngày` };
    if (diffDays <= 90) return { color: 'gold', text: `Còn ${diffDays} ngày` };
    return { color: 'green', text: `Còn ${diffDays} ngày` };
};

const getFileIcon = (loaiFile: string) => {
    const ext = loaiFile.toLowerCase();
    if (ext === 'pdf') return <FilePdfOutlined style={{ color: '#ff4d4f' }} />;
    return <FileImageOutlined style={{ color: '#1890ff' }} />;
};

const HoSoNhanVienManagement: React.FC = () => {
    const [employees, setEmployees] = useState<Employee[]>([]);
    const [departments, setDepartments] = useState<Department[]>([]);
    const [selectedDepartment, setSelectedDepartment] = useState<number | null>(null);
    const [loaiGiayToList, setLoaiGiayToList] = useState<LoaiGiayToDto[]>([]);
    const [giayToList, setGiayToList] = useState<GiayToDto[]>([]);
    const [allGiayToList, setAllGiayToList] = useState<GiayToDto[]>([]);
    const [pendingGiayToList, setPendingGiayToList] = useState<GiayToDto[]>([]);
    const [selectedEmployee, setSelectedEmployee] = useState<string | null>(null);
    const [searchEmployee, setSearchEmployee] = useState('');
    const [loading, setLoading] = useState(false);

    // Danh mục cho thông tin nhân viên
    const [chucDanhList, setChucDanhList] = useState<ChucDanh[]>([]);
    const [chucVuList, setChucVuList] = useState<ChucVu[]>([]);
    const [chuyenMonList, setChuyenMonList] = useState<ChuyenMon[]>([]);
    const [selectedEmployeeDetail, setSelectedEmployeeDetail] = useState<Employee | null>(null);

    // Modal states
    const [modalVisible, setModalVisible] = useState(false);
    const [editingGiayTo, setEditingGiayTo] = useState<GiayToDto | null>(null);
    const [previewVisible, setPreviewVisible] = useState(false);
    const [previewItem, setPreviewItem] = useState<GiayToDto | null>(null);
    const [previewBlobUrl, setPreviewBlobUrl] = useState<string | null>(null);
    const [replaceFileId, setReplaceFileId] = useState<number | null>(null);
    const [loaiGiayToListModalVisible, setLoaiGiayToListModalVisible] = useState(false);
    const [loaiGiayToFormModalVisible, setLoaiGiayToFormModalVisible] = useState(false);
    const [editingLoaiGiayTo, setEditingLoaiGiayTo] = useState<LoaiGiayToDto | null>(null);

    const [form] = Form.useForm();
    const [loaiGiayToForm] = Form.useForm();
    const [fileList, setFileList] = useState<any[]>([]);
    const listContainerRef = useRef<HTMLDivElement>(null);
    const [listHeight, setListHeight] = useState(400);

    // Anh dai dien
    const [anhDaiDien, setAnhDaiDien] = useState<NhanVienAnhDaiDienDto | null>(null);
    const [anhDaiDienBlobUrl, setAnhDaiDienBlobUrl] = useState<string | null>(null);
    const [anhDaiDienFileList, setAnhDaiDienFileList] = useState<any[]>([]);
    const [uploadingAnh, setUploadingAnh] = useState(false);

    useEffect(() => {
        const el = listContainerRef.current;
        if (!el) return;
        const update = () => setListHeight(el.clientHeight);
        update();
        const ro = new ResizeObserver(update);
        ro.observe(el);
        return () => ro.disconnect();
    }, []);

    const fetchEmployees = async () => {
        try {
            const res = await masterDataService.getEmployees();
            setEmployees(res.data);
        } catch {
            message.error('Không tải được danh sách nhân viên');
        }
    };

    const fetchDepartments = async () => {
        try {
            const res = await masterDataService.getDepartments();
            setDepartments(res.data);
        } catch {
            message.error('Không tải được danh sách khoa phòng');
        }
    };

    const fetchDanhMuc = async () => {
        try {
            const [chucDanhRes, chucVuRes, chuyenMonRes] = await Promise.all([
                masterDataService.getChucDanh(),
                masterDataService.getChucVu(),
                masterDataService.getChuyenMon(),
            ]);
            setChucDanhList(chucDanhRes.data);
            setChucVuList(chucVuRes.data);
            setChuyenMonList(chuyenMonRes.data);
        } catch {
            message.error('Không tải được danh mục');
        }
    };

    const fetchLoaiGiayTo = async () => {
        try {
            const res = await hoSoNhanVienService.getLoaiGiayToList();
            setLoaiGiayToList(res.data);
        } catch {
            message.error('Không tải được danh mục loại giấy tờ');
        }
    };

    const fetchAllGiayTo = async () => {
        try {
            const res = await hoSoNhanVienService.getGiayToList();
            setAllGiayToList(res.data);
        } catch {
            // không show error vì đây là dữ liệu phụ cho cảnh báo
        }
    };

    const fetchPendingGiayTo = async () => {
        try {
            const res = await hoSoNhanVienService.getPendingGiayToList();
            setPendingGiayToList(res.data);
        } catch {
            message.error('Không tải được danh sách giấy tờ chờ duyệt');
        }
    };

    const fetchGiayTo = async (maChamCong?: string) => {
        if (!maChamCong && !selectedEmployee) {
            setGiayToList([]);
            return;
        }
        try {
            setLoading(true);
            const res = await hoSoNhanVienService.getGiayToList(maChamCong || selectedEmployee || undefined);
            setGiayToList(res.data);
        } catch {
            message.error('Không tải được danh sách giấy tờ');
        } finally {
            setLoading(false);
        }
    };

    const fetchEmployeeDetail = async (maChamCong: string) => {
        try {
            // Lấy danh sách nhân viên với thông tin chi tiết
            const res = await masterDataService.getEmployees(true);
            const employee = res.data.find(e => e.macc === maChamCong);
            setSelectedEmployeeDetail(employee || null);
        } catch {
            message.error('Không tải được thông tin nhân viên');
        }
    };

    useEffect(() => {
        fetchEmployees();
        fetchDepartments();
        fetchLoaiGiayTo();
        fetchAllGiayTo();
        fetchPendingGiayTo();
        fetchDanhMuc();
    }, []);

    const fetchAnhDaiDien = async (maChamCong: string) => {
        try {
            const res = await hoSoNhanVienService.getAnhDaiDien(maChamCong);
            setAnhDaiDien(res.data);
            if (res.data) {
                const blob = await hoSoNhanVienService.getAnhDaiDienPreviewBlob(maChamCong);
                setAnhDaiDienBlobUrl(URL.createObjectURL(blob));
            } else {
                setAnhDaiDienBlobUrl(null);
            }
        } catch {
            setAnhDaiDien(null);
            setAnhDaiDienBlobUrl(null);
        }
    };

    useEffect(() => {
        if (selectedEmployee) {
            fetchGiayTo(selectedEmployee);
            fetchAnhDaiDien(selectedEmployee);
            fetchEmployeeDetail(selectedEmployee);
        } else {
            setGiayToList([]);
            setAnhDaiDien(null);
            setSelectedEmployeeDetail(null);
            if (anhDaiDienBlobUrl) URL.revokeObjectURL(anhDaiDienBlobUrl);
            setAnhDaiDienBlobUrl(null);
        }
    }, [selectedEmployee]);

    const filteredEmployees = useMemo(() => {
        return employees.filter(e => {
            if (selectedDepartment !== null && e.makk !== selectedDepartment) return false;
            if (!searchEmployee) return true;
            const s = searchEmployee.toLowerCase();
            return (
                (e.tennv || '').toLowerCase().includes(s) ||
                (e.macc || '').toLowerCase().includes(s)
            );
        });
    }, [employees, searchEmployee, selectedDepartment]);

    // Map giấy tờ theo nhân viên dùng để tính cảnh báo
    const giayToByEmployee = useMemo(() => {
        const map = new Map<string, GiayToDto[]>();
        for (const g of allGiayToList) {
            const list = map.get(g.maChamCong) || [];
            list.push(g);
            map.set(g.maChamCong, list);
        }
        return map;
    }, [allGiayToList]);

    // Map giấy tờ chờ duyệt theo nhân viên
    const pendingGiayToByEmployee = useMemo(() => {
        const map = new Map<string, GiayToDto[]>();
        for (const g of pendingGiayToList) {
            const list = map.get(g.maChamCong) || [];
            list.push(g);
            map.set(g.maChamCong, list);
        }
        return map;
    }, [pendingGiayToList]);

    // Tính cảnh báo cho 1 nhân viên: thiếu bắt buộc / hết hạn / sắp hết hạn / chờ duyệt
    const getEmployeeWarning = (macc: string): { color: string; text: string } | null => {
        const ds = giayToByEmployee.get(macc) || [];
        const pendingDs = pendingGiayToByEmployee.get(macc) || [];
        const now = dayjs();
        let hetHan = 0;
        let sapHetHan = 0;
        for (const g of ds) {
            if (!g.ngayHetHan) continue;
            const diff = dayjs(g.ngayHetHan).diff(now, 'day');
            if (diff < 0) hetHan++;
            else if (diff <= 30) sapHetHan++;
        }
        // Kiểm tra thiếu giấy tờ bắt buộc
        const daCoMaLoai = new Set(ds.map(g => g.maLoaiGiayTo));
        const batBuoc = loaiGiayToList.filter(l => l.batBuoc);
        const thieuBatBuoc = batBuoc.filter(l => !daCoMaLoai.has(l.maLoaiGiayTo)).length;

        const parts: string[] = [];
        if (thieuBatBuoc > 0) parts.push(`Thiếu ${thieuBatBuoc}`);
        if (hetHan > 0) parts.push(`${hetHan} HH`);
        if (sapHetHan > 0) parts.push(`${sapHetHan} sắp HH`);
        if (pendingDs.length > 0) parts.push(`${pendingDs.length} chờ duyệt`);

        if (parts.length === 0) return null;
        const color = thieuBatBuoc > 0 || hetHan > 0 ? 'red' : (pendingDs.length > 0 ? 'orange' : 'orange');
        return { color, text: parts.join(' | ') };
    };

    const showCreateModal = () => {
        if (!selectedEmployee) {
            message.warning('Vui lòng chọn nhân viên');
            return;
        }
        setEditingGiayTo(null);
        form.resetFields();
        setFileList([]);
        setModalVisible(true);
    };

    const showEditModal = (record: GiayToDto) => {
        setEditingGiayTo(record);
        form.setFieldsValue({
            maLoaiGiayTo: record.maLoaiGiayTo,
            ten: record.ten,
            maSo: record.maSo,
            noiCap: record.noiCap,
            ngayCap: record.ngayCap ? dayjs(record.ngayCap) : null,
            ngayHetHan: record.ngayHetHan ? dayjs(record.ngayHetHan) : null,
            ghiChu: record.ghiChu,
        });
        setFileList([]);
        setModalVisible(true);
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            setLoading(true);

            if (editingGiayTo) {
                const updateData: UpdateGiayToRequest = {
                    maLoaiGiayTo: values.maLoaiGiayTo,
                    ten: values.ten,
                    maSo: values.maSo,
                    noiCap: values.noiCap,
                    ngayCap: values.ngayCap ? values.ngayCap.format('YYYY-MM-DD') : null,
                    ngayHetHan: values.ngayHetHan ? values.ngayHetHan.format('YYYY-MM-DD') : null,
                    ghiChu: values.ghiChu,
                };
                await hoSoNhanVienService.updateGiayTo(editingGiayTo.id, updateData);
                message.success('Cập nhật giấy tờ thành công');
            } else {
                const file = fileList[0]?.originFileObj;
                if (!file) {
                    message.warning('Vui lòng chọn file');
                    setLoading(false);
                    return;
                }
                // Validate dung lượng file (max 10MB)
                const maxSize = 10 * 1024 * 1024; // 10MB
                if (file.size > maxSize) {
                    message.error(`File quá lớn. Vui lòng chọn file nhỏ hơn 10MB (hiện tại: ${(file.size / 1024 / 1024).toFixed(2)}MB)`);
                    setLoading(false);
                    return;
                }
                // Validate loại file (chỉ chấp PDF)
                const fileExt = file.name.split('.').pop()?.toLowerCase();
                if (fileExt !== 'pdf') {
                    message.error('Chỉ chấp file PDF');
                    setLoading(false);
                    return;
                }
                const createData: CreateGiayToRequest = {
                    maChamCong: selectedEmployee!,
                    maLoaiGiayTo: values.maLoaiGiayTo,
                    ten: values.ten,
                    maSo: values.maSo,
                    noiCap: values.noiCap,
                    ngayCap: values.ngayCap ? values.ngayCap.format('YYYY-MM-DD') : null,
                    ngayHetHan: values.ngayHetHan ? values.ngayHetHan.format('YYYY-MM-DD') : null,
                    ghiChu: values.ghiChu,
                };
                await hoSoNhanVienService.createGiayTo(createData, file);
                message.success('Tạo giấy tờ thành công');
            }

            setModalVisible(false);
            form.resetFields();
            setFileList([]);
            fetchGiayTo();
            fetchAllGiayTo();
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Thao tác thất bại');
        } finally {
            setLoading(false);
        }
    };

    const handleDelete = async (id: number) => {
        try {
            await hoSoNhanVienService.deleteGiayTo(id);
            message.success('Đã xóa giấy tờ');
            fetchGiayTo();
            fetchAllGiayTo();
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Xóa thất bại');
        }
    };

    const handleApproveGiayTo = async (id: number) => {
        try {
            await hoSoNhanVienService.approveGiayTo(id);
            message.success('Đã duyệt giấy tờ');
            fetchPendingGiayTo();
            fetchAllGiayTo();
            if (selectedEmployee) fetchGiayTo(selectedEmployee);
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Duyệt thất bại');
        }
    };

    const handleRejectGiayTo = async (id: number) => {
        try {
            await hoSoNhanVienService.deleteGiayTo(id);
            message.success('Đã từ chối và xóa giấy tờ');
            fetchPendingGiayTo();
            fetchAllGiayTo();
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Từ chối thất bại');
        }
    };

    const handlePreview = async (record: GiayToDto) => {
        setPreviewItem(record);
        setPreviewVisible(true);
        setPreviewBlobUrl(null);
        try {
            const blob = await hoSoNhanVienService.getPreviewBlob(record.id);
            setPreviewBlobUrl(URL.createObjectURL(blob));
        } catch {
            message.error('Không tải được file để xem');
        }
    };

    const closePreview = () => {
        if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
        setPreviewBlobUrl(null);
        setPreviewVisible(false);
        setPreviewItem(null);
    };

    const handleReplaceFile = async (id: number) => {
        if (fileList.length === 0) {
            message.warning('Vui lòng chọn file mới');
            return;
        }
        try {
            setLoading(true);
            const file = fileList[0].originFileObj;
            await hoSoNhanVienService.replaceGiayToFile(id, file);
            message.success('Đã thay thế file');
            setReplaceFileId(null);
            setFileList([]);
            fetchGiayTo();
            fetchAllGiayTo();
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Thay thế file thất bại');
        } finally {
            setLoading(false);
        }
    };

    const handleDownload = async (record: GiayToDto) => {
        try {
            const blob = await hoSoNhanVienService.getDownloadBlob(record.id);
            const url = URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.target = '_blank';
            link.download = record.tenFileGoc;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            URL.revokeObjectURL(url);
        } catch {
            message.error('Không tải được file');
        }
    };

    // ===== Anh dai dien =====
    const handleUploadAnhDaiDien = async () => {
        if (!selectedEmployee) return;
        const file = anhDaiDienFileList[0]?.originFileObj;
        if (!file) {
            message.warning('Vui lòng chọn ảnh');
            return;
        }
        try {
            setUploadingAnh(true);
            await hoSoNhanVienService.uploadAnhDaiDien(selectedEmployee, file);
            message.success('Đã cập nhật ảnh đại diện');
            setAnhDaiDienFileList([]);
            if (anhDaiDienBlobUrl) URL.revokeObjectURL(anhDaiDienBlobUrl);
            await fetchAnhDaiDien(selectedEmployee);
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Tải ảnh thất bại');
        } finally {
            setUploadingAnh(false);
        }
    };

    const handleDeleteAnhDaiDien = async () => {
        if (!selectedEmployee) return;
        try {
            await hoSoNhanVienService.deleteAnhDaiDien(selectedEmployee);
            message.success('Đã xóa ảnh đại diện');
            if (anhDaiDienBlobUrl) URL.revokeObjectURL(anhDaiDienBlobUrl);
            setAnhDaiDien(null);
            setAnhDaiDienBlobUrl(null);
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Xóa ảnh thất bại');
        }
    };

    // LoaiGiayTo management
    const showLoaiGiayToListModal = () => {
        setLoaiGiayToListModalVisible(true);
    };

    const showCreateLoaiGiayToModal = () => {
        setEditingLoaiGiayTo(null);
        loaiGiayToForm.resetFields();
        loaiGiayToForm.setFieldsValue({
            batBuoc: false,
            canhBaoHetHan: true,
        });
        setLoaiGiayToFormModalVisible(true);
    };

    const showEditLoaiGiayToModal = (record: LoaiGiayToDto) => {
        setEditingLoaiGiayTo(record);
        loaiGiayToForm.setFieldsValue({
            tenLoai: record.tenLoai,
            moTa: record.moTa,
            batBuoc: record.batBuoc,
            canhBaoHetHan: record.canhBaoHetHan,
            thuTuHienThi: record.thuTuHienThi,
            active: record.active,
        });
        setLoaiGiayToFormModalVisible(true);
    };

    const handleLoaiGiayToFormOk = async () => {
        try {
            const values = await loaiGiayToForm.validateFields();
            setLoading(true);

            if (editingLoaiGiayTo) {
                // Update
                const data: UpdateLoaiGiayToRequest = {
                    tenLoai: values.tenLoai,
                    moTa: values.moTa,
                    batBuoc: values.batBuoc,
                    canhBaoHetHan: values.canhBaoHetHan,
                    thuTuHienThi: values.thuTuHienThi,
                    active: values.active ?? true,
                };
                await hoSoNhanVienService.updateLoaiGiayTo(editingLoaiGiayTo.maLoaiGiayTo, data);
                message.success('Cập nhật loại giấy tờ thành công');
            } else {
                // Create
                const data: CreateLoaiGiayToRequest = {
                    tenLoai: values.tenLoai,
                    moTa: values.moTa,
                    batBuoc: values.batBuoc,
                    canhBaoHetHan: values.canhBaoHetHan,
                    thuTuHienThi: values.thuTuHienThi,
                };
                await hoSoNhanVienService.createLoaiGiayTo(data);
                message.success('Tạo loại giấy tờ thành công');
            }

            setLoaiGiayToFormModalVisible(false);
            loaiGiayToForm.resetFields();
            fetchLoaiGiayTo();
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Thao tác thất bại');
        } finally {
            setLoading(false);
        }
    };

    const handleDeleteLoaiGiayTo = async (id: number) => {
        try {
            await hoSoNhanVienService.deleteLoaiGiayTo(id);
            message.success('Đã xóa loại giấy tờ');
            fetchLoaiGiayTo();
        } catch (error: any) {
            message.error(error.response?.data?.message || 'Xóa thất bại');
        }
    };

    const handleExportExcel = async () => {
        try {
            setLoading(true);
            // Fetch lại dữ liệu với includeDetails=true để lấy đầy đủ thông tin
            const res = await masterDataService.getEmployees(true);
            const allEmployeesWithDetails = res.data;

            const dataToExport = selectedDepartment !== null
                ? allEmployeesWithDetails.filter(e => e.makk === selectedDepartment)
                : allEmployeesWithDetails;

            if (dataToExport.length === 0) {
                message.warning('Không có dữ liệu để xuất');
                return;
            }

            const khoaName = selectedDepartment !== null
                ? departments.find(d => d.makk === selectedDepartment)?.tenkk || 'Khoa'
                : 'ToanVien';

        let html = `<table border="1">`;
        html += `<thead><tr>`;
        html += `<th>STT</th>`;
        html += `<th>Mã NV</th>`;
        html += `<th>Họ tên</th>`;
        html += `<th>Ngày sinh</th>`;
        html += `<th>Giới tính</th>`;
        html += `<th>CMND/CCCD</th>`;
        html += `<th>Nơi cấp</th>`;
        html += `<th>Ngày cấp</th>`;
        html += `<th>Email</th>`;
        html += `<th>Điện thoại</th>`;
        html += `<th>Khoa</th>`;
        html += `<th>Trình độ chuyên môn</th>`;
        html += `<th>Chức vụ</th>`;
        html += `<th>Chuyên môn</th>`;
        html += `<th>Chứng chỉ nghề</th>`;
        html += `<th>Ngày cấp CCHN</th>`;
        html += `<th>Mã BHXH</th>`;
        html += `<th>Tình trạng</th>`;
        html += `</tr></thead>`;
        html += `<tbody>`;

        dataToExport.forEach((emp, index) => {
            html += `<tr>`;
            html += `<td>${index + 1}</td>`;
            html += `<td>${emp.macc || ''}</td>`;
            html += `<td>${emp.tennv || ''}</td>`;
            html += `<td>${emp.ngaysinh ? dayjs(emp.ngaysinh).format('DD/MM/YYYY') : ''}</td>`;
            html += `<td>${emp.maphai === 1 ? 'Nam' : emp.maphai === 2 ? 'Nữ' : ''}</td>`;
            html += `<td>${emp.chungminhthu || ''}</td>`;
            html += `<td>${emp.noicapcmt || ''}</td>`;
            html += `<td>${emp.ngaycapcmt ? dayjs(emp.ngaycapcmt).format('DD/MM/YYYY') : ''}</td>`;
            html += `<td>${emp.email || ''}</td>`;
            html += `<td>${emp.dienthoai || ''}</td>`;
            html += `<td>${departments.find(d => d.makk === emp.makk)?.tenkk || ''}</td>`;
            html += `<td>${chucDanhList.find(c => c.machucdanh === emp.machucdanh)?.tenchucdanh || ''}</td>`;
            html += `<td>${chucVuList.find(c => c.machucvu === emp.machucvu)?.tenchucvu || ''}</td>`;
            html += `<td>${chuyenMonList.find(c => c.machuyenmon === emp.machuyenmon)?.tenchuyenmon || ''}</td>`;
            html += `<td>${emp.chungchinghe || ''}</td>`;
            html += `<td>${emp.ngaycapcc ? dayjs(emp.ngaycapcc).format('DD/MM/YYYY') : ''}</td>`;
            html += `<td>${emp.mabhxh || ''}</td>`;
            html += `<td>${emp.nghiviec ? 'Đã nghỉ việc' : 'Đang làm việc'}</td>`;
            html += `</tr>`;
        });

        html += `</tbody></table>`;

        const now = dayjs();
        const timestamp = now.format('YYYYMMDD_HHmmss');
        const blob = new Blob(['\ufeff', html], { type: 'application/vnd.ms-excel' });
        const url = URL.createObjectURL(blob);
        const link = document.createElement('a');
        link.href = url;
        link.download = `DanhSachNhanVien_${khoaName}_${timestamp}.xls`;
        link.click();
        URL.revokeObjectURL(url);
        message.success('Đã xuất file Excel');
        } catch (error) {
            message.error('Không thể xuất file Excel');
        } finally {
            setLoading(false);
        }
    };

    const columns = [
        {
            title: 'Loại giấy tờ',
            dataIndex: 'tenLoai',
            key: 'tenLoai',
            width: 150,
            render: (text: string, record: GiayToDto) => (
                <Space>
                    {getFileIcon(record.loaiFile)}
                    <span>{text}</span>
                </Space>
            ),
        },
        {
            title: 'Tên',
            dataIndex: 'ten',
            key: 'ten',
            width: 180,
        },
        {
            title: 'Số giấy tờ',
            dataIndex: 'maSo',
            key: 'maSo',
            width: 120,
        },
        {
            title: 'Nơi cấp',
            dataIndex: 'noiCap',
            key: 'noiCap',
            width: 130,
        },
        {
            title: 'Ngày cấp',
            dataIndex: 'ngayCap',
            key: 'ngayCap',
            width: 80,
            render: (v: string | null) => v ? dayjs(v).format('DD/MM/YYYY') : '-',
        },
        {
            title: 'Ngày hết hạn',
            dataIndex: 'ngayHetHan',
            key: 'ngayHetHan',
            width: 80,
            render: (v: string | null) => v ? dayjs(v).format('DD/MM/YYYY') : 'Vĩnh viễn',
        },
        {
            title: 'Trạng thái',
            key: 'expiry',
            width: 80,
            render: (_: any, record: GiayToDto) => {
                const status = getExpiryStatus(record.ngayHetHan);
                return <Tag color={status.color}>{status.text}</Tag>;
            },
        },
        {
            title: 'File',
            dataIndex: 'tenFileGoc',
            key: 'tenFileGoc',
            ellipsis: true,
            width: 100,
        },
        {
            title: 'Thao tác',
            key: 'actions',
            width: 80,
            fixed: 'right' as const,
            render: (_: any, record: GiayToDto) => (
                <Dropdown
                    menu={{
                        items: [
                            {
                                key: 'view',
                                icon: <EyeOutlined />,
                                label: 'Xem',
                                onClick: () => handlePreview(record),
                            },
                            {
                                key: 'download',
                                icon: <DownloadOutlined />,
                                label: 'Tải',
                                onClick: () => handleDownload(record),
                            },
                            {
                                key: 'edit',
                                icon: <EditOutlined />,
                                label: 'Sửa',
                                onClick: () => showEditModal(record),
                            },
                            {
                                type: 'divider' as const,
                            },
                            {
                                key: 'delete',
                                icon: <DeleteOutlined />,
                                label: 'Xóa',
                                danger: true,
                                onClick: () => {
                                    if (window.confirm('Bạn có chắc chắn muốn xóa giấy tờ này không?')) {
                                        handleDelete(record.id);
                                    }
                                },
                            },
                        ],
                    }}
                    trigger={['click']}
                >
                    <Button type="link" size="small" icon={<SettingOutlined />} />
                </Dropdown>
            ),
        },
    ];

    const selectedEmp = employees.find(e => e.macc === selectedEmployee);

    return (
        <div style={{ padding: 16 }}>
            <Row gutter={16} style={{ height: 'calc(100vh - 140px)' }}>
                {/* Left: Employee list */}
                <Col span={3}>
                    <Card
                        title="Danh sách nhân viên"
                        size="small"
                        style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                        extra={
                            <Button size="small" icon={<ReloadOutlined />} onClick={fetchEmployees} />
                        }
                        styles={{ body: { flex: 1, overflow: 'hidden', padding: 8, display: 'flex', flexDirection: 'column' } }}
                    >
                        <Select
                            placeholder="Tất cả khoa phòng"
                            value={selectedDepartment ?? undefined}
                            onChange={v => setSelectedDepartment(v ?? null)}
                            style={{ width: '100%', marginBottom: 8, flexShrink: 0 }}
                            allowClear
                            size="small"
                        >
                            {departments.map(d => (
                                <Option key={d.makk} value={d.makk}>{d.tenkk}</Option>
                            ))}
                        </Select>
                        <Input
                            placeholder="Tìm kiếm tên/mã"
                            value={searchEmployee}
                            onChange={e => setSearchEmployee(e.target.value)}
                            style={{ marginBottom: 8, flexShrink: 0 }}
                            allowClear
                            size="small"
                        />
                        <div ref={listContainerRef} style={{ flex: 1, minHeight: 0, overflow: 'hidden' }}>
                            <VirtualList
                                data={filteredEmployees}
                                height={listHeight}
                                itemHeight={56}
                                itemKey="macc"
                                style={{ overflowY: 'auto', overflowX: 'hidden' }}
                            >
                                {(item, _, { style }) => {
                                    const warning = getEmployeeWarning(item.macc);
                                    const isSelected = selectedEmployee === item.macc;
                                    const bg = isSelected
                                        ? '#e6f7ff'
                                        : warning
                                            ? (warning.color === 'red' ? '#fff1f0' : '#fff7e6')
                                            : 'transparent';
                                    return (
                                        <div
                                            onClick={() => setSelectedEmployee(item.macc)}
                                            style={{
                                                ...style,
                                                cursor: 'pointer',
                                                background: bg,
                                                padding: '6px 8px',
                                                borderRadius: 4,
                                                marginBottom: 2,
                                                boxSizing: 'border-box',
                                            }}
                                        >
                                            <div style={{ fontWeight: isSelected ? 600 : 500, fontSize: 13, lineHeight: 1.3 }}>
                                                {item.tennv}
                                            </div>
                                            <div style={{ fontSize: 11, color: '#888' }}>{item.macc}</div>
                                            {warning && (
                                                <div style={{ fontSize: 11, color: warning.color === 'red' ? '#cf1322' : '#d46b08', marginTop: 2, lineHeight: 1.2 }}>
                                                    {warning.text}
                                                </div>
                                            )}
                                        </div>
                                    );
                                }}
                            </VirtualList>
                        </div>
                    </Card>
                </Col>

                {/* Right: Documents */}
                <Col span={21}>
                    <Card
                        title={
                            selectedEmp
                                ? `Hồ sơ: ${selectedEmp.tennv} (${selectedEmp.macc})`
                                : 'Hồ sơ nhân viên'
                        }
                        size="small"
                        style={{ height: '100%', overflow: 'hidden', display: 'flex', flexDirection: 'column' }}
                        extra={
                            <Space>
                                <Button
                                    size="small"
                                    icon={<SettingOutlined />}
                                    onClick={showLoaiGiayToListModal}
                                >
                                    Danh mục
                                </Button>
                                <Button
                                    size="small"
                                    icon={<FileExcelOutlined />}
                                    onClick={handleExportExcel}
                                >
                                    Xuất Excel
                                </Button>
                                <Button
                                    type="primary"
                                    size="small"
                                    icon={<PlusOutlined />}
                                    onClick={showCreateModal}
                                    disabled={!selectedEmployee}
                                >
                                    Thêm giấy tờ
                                </Button>
                            </Space>
                        }
                        styles={{ body: { flex: 1, overflow: 'auto' } }}
                    >
                        {!selectedEmployee ? (
                            <div style={{ textAlign: 'center', color: '#999', padding: 40 }}>
                                Vui lòng chọn nhân viên từ danh sách bên trái
                            </div>
                        ) : (
                            <>
                                {/* Anh dai dien */}
                                <div style={{ display: 'flex', alignItems: 'flex-start', gap: 16, marginBottom: 16, padding: 12, background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                                    <div style={{ width: 96, height: 120, borderRadius: 4, overflow: 'hidden', border: '1px solid #d9d9d9', background: '#fff', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                        {anhDaiDienBlobUrl ? (
                                            <img src={anhDaiDienBlobUrl} alt="Ảnh đại diện" style={{ width: '100%', height: '100%', objectFit: 'cover' }} />
                                        ) : (
                                            <span style={{ color: '#bbb', fontSize: 11, textAlign: 'center', padding: 4 }}>Chưa có ảnh</span>
                                        )}
                                    </div>
                                    <div style={{ flex: 1 }}>
                                        <div style={{ fontWeight: 600, marginBottom: 6 }}>Ảnh hồ sơ</div>
                                        <div style={{ color: '#888', fontSize: 12, marginBottom: 8 }}>
                                            {anhDaiDien ? `File: ${anhDaiDien.tenFileGoc}` : 'Nhân viên chưa có ảnh hồ sơ'}
                                        </div>
                                        <Space size="small">
                                            <Upload
                                                beforeUpload={() => false}
                                                fileList={anhDaiDienFileList}
                                                onChange={({ fileList: fl }) => setAnhDaiDienFileList(fl.slice(-1))}
                                                accept=".jpg,.jpeg,.png"
                                                maxCount={1}
                                                showUploadList={false}
                                            >
                                                <Button size="small" icon={<PlusOutlined />} loading={uploadingAnh}>
                                                    {anhDaiDien ? 'Đổi ảnh' : 'Tải ảnh'}
                                                </Button>
                                            </Upload>
                                            {anhDaiDienFileList.length > 0 && (
                                                <Button size="small" type="primary" loading={uploadingAnh} onClick={handleUploadAnhDaiDien}>
                                                    Lưu ảnh
                                                </Button>
                                            )}
                                            {anhDaiDien && (
                                                <Popconfirm title="Xóa ảnh đại diện này?" onConfirm={handleDeleteAnhDaiDien} okText="Xóa" cancelText="Hủy">
                                                    <Button size="small" danger icon={<DeleteOutlined />}>Xóa</Button>
                                                </Popconfirm>
                                            )}
                                        </Space>
                                        <div style={{ marginTop: 6, color: '#aaa', fontSize: 11 }}>Chỉ chấp nhận ảnh JPG, PNG - tối đa 10MB</div>
                                    </div>
                                </div>

                                {/* Thong tin nhan vien */}
                                {selectedEmployeeDetail && (
                                    <div style={{ marginTop: 16, padding: 12, background: '#fafafa', borderRadius: 6, border: '1px solid #f0f0f0' }}>
                                        <div style={{ fontWeight: 600, marginBottom: 12, color: '#1890ff', fontSize: 13 }}>Thông tin nhân viên</div>
                                        <div style={{
                                            display: 'grid',
                                            gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
                                            gap: '8px 16px',
                                            fontSize: 12,
                                            lineHeight: 1.6
                                        }}>
                                            <div><strong>Mã NV:</strong> {selectedEmployeeDetail.macc}</div>
                                            <div><strong>Họ tên:</strong> {selectedEmployeeDetail.tennv}</div>
                                            <div><strong>Ngày sinh:</strong> {selectedEmployeeDetail.ngaysinh ? dayjs(selectedEmployeeDetail.ngaysinh).format('DD/MM/YYYY') : '-'}</div>
                                            <div><strong>Giới tính:</strong> {selectedEmployeeDetail.maphai === 1 ? 'Nam' : selectedEmployeeDetail.maphai === 2 ? 'Nữ' : '-'}</div>
                                            <div><strong>CMND/CCCD:</strong> {selectedEmployeeDetail.chungminhthu || '-'}</div>
                                            <div><strong>Nơi cấp:</strong> {selectedEmployeeDetail.noicapcmt || '-'}</div>
                                            <div><strong>Ngày cấp:</strong> {selectedEmployeeDetail.ngaycapcmt ? dayjs(selectedEmployeeDetail.ngaycapcmt).format('DD/MM/YYYY') : '-'}</div>
                                            <div><strong>Email:</strong> {selectedEmployeeDetail.email || '-'}</div>
                                            <div><strong>Điện thoại:</strong> {selectedEmployeeDetail.dienthoai || '-'}</div>
                                            <div><strong>Khoa:</strong> {departments.find(d => d.makk === selectedEmployeeDetail.makk)?.tenkk || '-'}</div>
                                            <div><strong>Trình độ chuyên môn:</strong> {chucDanhList.find(c => c.machucdanh === selectedEmployeeDetail.machucdanh)?.tenchucdanh || '-'}</div>
                                            <div><strong>Chức vụ:</strong> {chucVuList.find(c => c.machucvu === selectedEmployeeDetail.machucvu)?.tenchucvu || '-'}</div>
                                            <div><strong>Chuyên môn:</strong> {chuyenMonList.find(c => c.machuyenmon === selectedEmployeeDetail.machuyenmon)?.tenchuyenmon || '-'}</div>
                                            <div><strong>Chứng chỉ nghề:</strong> {selectedEmployeeDetail.chungchinghe || '-'}</div>
                                            <div><strong>Ngày cấp CCHN:</strong> {selectedEmployeeDetail.ngaycapcc ? dayjs(selectedEmployeeDetail.ngaycapcc).format('DD/MM/YYYY') : '-'}</div>
                                            <div><strong>Mã BHXH:</strong> {selectedEmployeeDetail.mabhxh || '-'}</div>
                                            <div><strong>Tình trạng:</strong> {selectedEmployeeDetail.nghiviec ? <Tag color="red">Đã nghỉ việc</Tag> : <Tag color="green">Đang làm việc</Tag>}</div>
                                        </div>
                                    </div>
                                )}

                                <Tabs defaultActiveKey="active">
                                    <TabPane tab="Giấy tờ đã duyệt" key="active">
                                        <Table
                                            columns={columns}
                                            dataSource={giayToList}
                                            rowKey="id"
                                            loading={loading}
                                            size="small"
                                            pagination={{ pageSize: 10 }}
                                            scroll={{ x: 1200 }}
                                        />
                                    </TabPane>
                                    <TabPane tab={<Badge count={selectedEmployee ? pendingGiayToList.filter(g => g.maChamCong === selectedEmployee).length : 0} offset={[10, 0]}>Giấy tờ chờ duyệt</Badge>} key="pending">
                                        <Table
                                            columns={[
                                                ...columns.slice(0, -1),
                                                {
                                                    title: 'Thao tác',
                                                    key: 'actions',
                                                    width: 80,
                                                    fixed: 'right' as const,
                                                    render: (_: any, record: GiayToDto) => (
                                                        <Dropdown
                                                            menu={{
                                                                items: [
                                                                    {
                                                                        key: 'view',
                                                                        icon: <EyeOutlined />,
                                                                        label: 'Xem',
                                                                        onClick: () => handlePreview(record),
                                                                    },
                                                                    {
                                                                        key: 'download',
                                                                        icon: <DownloadOutlined />,
                                                                        label: 'Tải',
                                                                        onClick: () => handleDownload(record),
                                                                    },
                                                                    {
                                                                        type: 'divider' as const,
                                                                    },
                                                                    {
                                                                        key: 'approve',
                                                                        icon: <CheckOutlined />,
                                                                        label: 'Duyệt',
                                                                        onClick: () => handleApproveGiayTo(record.id),
                                                                    },
                                                                    {
                                                                        key: 'reject',
                                                                        icon: <CloseOutlined />,
                                                                        label: 'Từ chối',
                                                                        danger: true,
                                                                        onClick: () => {
                                                                            if (window.confirm('Bạn có chắc chắn muốn từ chối và xóa giấy tờ này không?')) {
                                                                                handleRejectGiayTo(record.id);
                                                                            }
                                                                        },
                                                                    },
                                                                ],
                                                            }}
                                                            trigger={['click']}
                                                        >
                                                            <Button type="link" size="small" icon={<SettingOutlined />} />
                                                        </Dropdown>
                                                    ),
                                                },
                                            ]}
                                            dataSource={selectedEmployee ? pendingGiayToList.filter(g => g.maChamCong === selectedEmployee) : []}
                                            rowKey="id"
                                            loading={loading}
                                            size="small"
                                            pagination={{ pageSize: 10 }}
                                            scroll={{ x: 1200 }}
                                        />
                                    </TabPane>
                                </Tabs>
                            </>
                        )}
                    </Card>
                </Col>
            </Row>

            {/* Modal: Create/Edit GiayTo */}
            <Modal
                title={editingGiayTo ? 'Sửa giấy tờ' : 'Thêm giấy tờ'}
                open={modalVisible}
                onOk={handleModalOk}
                onCancel={() => { setModalVisible(false); form.resetFields(); setFileList([]); }}
                confirmLoading={loading}
                width={600}
            >
                <Form form={form} layout="vertical">
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="maLoaiGiayTo" label="Loại giấy tờ" rules={[{ required: true, message: 'Vui lòng chọn loại' }]}>
                                <Select placeholder="Chọn loại giấy tờ">
                                    {loaiGiayToList.map(l => (
                                        <Option key={l.maLoaiGiayTo} value={l.maLoaiGiayTo}>{l.tenLoai}</Option>
                                    ))}
                                </Select>
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="ten" label="Tên">
                                <Input placeholder="Tên cụ thể của giấy tờ" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="maSo" label="Số giấy tờ">
                                <Input placeholder="Số trên giấy tờ" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="noiCap" label="Nơi cấp">
                                <Input placeholder="Nơi cấp" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="ngayCap" label="Ngày cấp">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="ngayHetHan" label="Ngày hết hạn">
                                <DatePicker style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Form.Item name="ghiChu" label="Ghi chú">
                        <Input.TextArea rows={2} />
                    </Form.Item>
                    {!editingGiayTo && (
                        <Form.Item label="File (PDF - tối đa 10MB)" rules={[{ required: true, message: 'Vui lòng chọn file' }]}>
                            <Upload
                                beforeUpload={() => false}
                                fileList={fileList}
                                onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
                                accept=".pdf"
                                maxCount={1}
                            >
                                <Button icon={<PlusOutlined />}>Chọn file PDF</Button>
                            </Upload>
                        </Form.Item>
                    )}
                </Form>
            </Modal>

            {/* Modal: Preview */}
            <Modal
                title={previewItem?.tenLoai}
                open={previewVisible}
                onCancel={closePreview}
                footer={
                    previewItem ? (
                        <Space>
                            <Button icon={<DownloadOutlined />} onClick={() => previewItem && handleDownload(previewItem)}>
                                Tải về
                            </Button>
                            <Button
                                icon={<SwapOutlined />}
                                onClick={() => {
                                    if (previewBlobUrl) URL.revokeObjectURL(previewBlobUrl);
                                    setPreviewBlobUrl(null);
                                    setPreviewVisible(false);
                                    setReplaceFileId(previewItem!.id);
                                }}
                            >
                                Thay thế file
                            </Button>
                        </Space>
                    ) : null
                }
                width={800}
            >
                {previewItem && (
                    <div style={{ textAlign: 'center' }}>
                        {!previewBlobUrl ? (
                            <div style={{ padding: 40, color: '#999' }}>Đang tải file...</div>
                        ) : ['jpg', 'jpeg', 'png'].includes(previewItem.loaiFile.toLowerCase()) ? (
                            <img
                                src={previewBlobUrl}
                                alt={previewItem.tenFileGoc}
                                style={{ maxWidth: '100%', maxHeight: '70vh' }}
                            />
                        ) : (
                            <iframe
                                src={previewBlobUrl}
                                title={previewItem.tenFileGoc}
                                style={{ width: '100%', height: '70vh', border: 'none' }}
                            />
                        )}
                        <div style={{ marginTop: 8, color: '#666', fontSize: 12 }}>
                            File: {previewItem.tenFileGoc}
                        </div>
                    </div>
                )}
            </Modal>

            {/* Modal: Replace file */}
            <Modal
                title="Thay thế file"
                open={replaceFileId !== null}
                onOk={() => replaceFileId && handleReplaceFile(replaceFileId)}
                onCancel={() => { setReplaceFileId(null); setFileList([]); }}
                confirmLoading={loading}
            >
                <Upload
                    beforeUpload={() => false}
                    fileList={fileList}
                    onChange={({ fileList: fl }) => setFileList(fl.slice(-1))}
                    accept=".pdf"
                    maxCount={1}
                >
                    <Button icon={<PlusOutlined />}>Chọn file PDF mới</Button>
                </Upload>
                <div style={{ marginTop: 8, color: '#888', fontSize: 12 }}>
                    File cũ sẽ bị xóa và thay thế bằng file mới (chỉ chấp nhận PDF).
                </div>
            </Modal>

            {/* Modal: Danh sách LoaiGiayTo */}
            <Modal
                title="Danh mục loại giấy tờ"
                open={loaiGiayToListModalVisible}
                onCancel={() => setLoaiGiayToListModalVisible(false)}
                footer={
                    <Button type="primary" icon={<PlusOutlined />} onClick={showCreateLoaiGiayToModal}>
                        Thêm loại giấy tờ mới
                    </Button>
                }
                width={900}
            >
                <Table
                    dataSource={loaiGiayToList}
                    rowKey="maLoaiGiayTo"
                    size="small"
                    pagination={{ pageSize: 10 }}
                    columns={[
                        { title: 'Tên loại', dataIndex: 'tenLoai', key: 'tenLoai', width: 200 },
                        { title: 'Mô tả', dataIndex: 'moTa', key: 'moTa', ellipsis: true },
                        {
                            title: 'Bắt buộc', dataIndex: 'batBuoc', key: 'batBuoc', width: 90,
                            render: (v: boolean) => v ? <Tag color="red">Có</Tag> : <Tag>Không</Tag>
                        },
                        {
                            title: 'Cảnh báo hạn', dataIndex: 'canhBaoHetHan', key: 'canhBaoHetHan', width: 110,
                            render: (v: boolean) => v ? <Tag color="orange">Có</Tag> : <Tag>Không</Tag>
                        },
                        { title: 'Thứ tự', dataIndex: 'thuTuHienThi', key: 'thuTuHienThi', width: 80 },
                        {
                            title: 'Trạng thái', dataIndex: 'active', key: 'active', width: 90,
                            render: (v: boolean) => v ? <Tag color="green">Hoạt động</Tag> : <Tag color="default">Khóa</Tag>
                        },
                        {
                            title: 'Thao tác', key: 'actions', width: 120, fixed: 'right' as const,
                            render: (_: any, record: LoaiGiayToDto) => (
                                <Space size="small">
                                    <Button type="link" size="small" icon={<EditOutlined />} onClick={() => showEditLoaiGiayToModal(record)}>
                                        Sửa
                                    </Button>
                                    <Popconfirm title="Xóa loại này?" onConfirm={() => handleDeleteLoaiGiayTo(record.maLoaiGiayTo)} okText="Xóa" cancelText="Hủy">
                                        <Button type="link" size="small" danger icon={<DeleteOutlined />} />
                                    </Popconfirm>
                                </Space>
                            )
                        },
                    ]}
                />
            </Modal>

            {/* Modal: Form Thêm/Sửa LoaiGiayTo */}
            <Modal
                title={editingLoaiGiayTo ? 'Sửa loại giấy tờ' : 'Thêm loại giấy tờ mới'}
                open={loaiGiayToFormModalVisible}
                onOk={handleLoaiGiayToFormOk}
                onCancel={() => {
                    setLoaiGiayToFormModalVisible(false);
                    loaiGiayToForm.resetFields();
                    setEditingLoaiGiayTo(null);
                }}
                confirmLoading={loading}
                width={600}
            >
                <Form form={loaiGiayToForm} layout="vertical">
                    <Row gutter={16}>
                        <Col span={12}>
                            <Form.Item name="tenLoai" label="Tên loại giấy tờ" rules={[{ required: true, message: 'Vui lòng nhập tên loại' }]}>
                                <Input placeholder="Nhập tên loại giấy tờ" />
                            </Form.Item>
                        </Col>
                        <Col span={12}>
                            <Form.Item name="thuTuHienThi" label="Thứ tự hiển thị">
                                <InputNumber placeholder="Thứ tự" min={0} style={{ width: '100%' }} />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={24}>
                            <Form.Item name="moTa" label="Mô tả">
                                <Input.TextArea rows={2} placeholder="Mô tả về loại giấy tờ" />
                            </Form.Item>
                        </Col>
                    </Row>
                    <Row gutter={16}>
                        <Col span={8}>
                            <Form.Item name="batBuoc" label="Bắt buộc" valuePropName="checked">
                                <Switch checkedChildren="Có" unCheckedChildren="Không" />
                            </Form.Item>
                        </Col>
                        <Col span={8}>
                            <Form.Item name="canhBaoHetHan" label="Cảnh báo hết hạn" valuePropName="checked">
                                <Switch checkedChildren="Có" unCheckedChildren="Không" />
                            </Form.Item>
                        </Col>
                        {editingLoaiGiayTo && (
                            <Col span={8}>
                                <Form.Item name="active" label="Trạng thái" valuePropName="checked">
                                    <Switch checkedChildren="Hoạt động" unCheckedChildren="Khóa" />
                                </Form.Item>
                            </Col>
                        )}
                    </Row>
                </Form>
            </Modal>
        </div>
    );
};

export default HoSoNhanVienManagement;
